import Vue from "vue";
import { auth, db } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import VueRouter from "vue-router";

// General Views
import LoginView from "@/views/LoginView.vue";
import HomeView from "@/views/HomeView.vue";
import ProfileView from "@/views/ProfileView.vue";
import SearchView from "@/views/SearchView.vue";
import ChatView from "@/views/ChatView.vue";
import MessagesView from "@/views/MessagesView.vue";
import GroupView from "@/views/GroupView.vue";
import GroupListView from "@/views/GroupView.vue";
import GroupDetailView from "@/views/GroupDetailView.vue";
import MemberDetailView from "@/views/MemberDetailView.vue";
import AddMemberView from "@/views/AddMemberView.vue";
import MemberFriendsView from '@/views/MemberFriendsView.vue';
import PostsView from "@/views/PostsView.vue";
import MemberSettingsView from "@/views/MemberSettingsView.vue";
import AdminHomeView from "@/views/AdminHomeView.vue";
import AdminSearchView from "@/views/AdminSearchView.vue";
import AdminSettingsView from "@/views/AdminSettingsView.vue";
import AdminWaitlist from "@/views/AdminWaitlist.vue";
import SecuritySettingsView from '../views/SecuritySettingsView.vue'
import PrivacyView from "@/views/PrivacyView.vue";
import TermsView from "@/views/TermsView.vue";
import GuidelinesView from "@/views/GuidelinesView.vue";
import AboutView from "@/views/AboutView.vue";
import FaqView from "@/views/FaqView.vue";
import DeleteAccountView from "@/views/DeleteAccountView.vue";

Vue.use(VueRouter);

const routes = [
  { path: "/", name: "Login", component: LoginView },
  { path: "/home", name: "Home", component: HomeView },
  { path: "/admin-home", name: "AdminHome", component: AdminHomeView },
  { path: "/admin/waitlist", name: "AdminWaitlist", component: AdminWaitlist, meta: { requiresAdmin: true } },
  {
    path: '/admin-access-requests',
    name: 'AdminAccessRequests',
    component: () => import('@/views/AdminAccessRequests.vue'),
  },
  // Music videos (their own thing; /music-video/new must stay above /music-video/:id)
  { path: '/music-videos', name: 'MusicVideos', component: () => import('@/views/MusicVideosView.vue') },
  { path: '/music-video/new', name: 'MusicVideoNew', component: () => import('@/views/MusicVideoNewView.vue') },
  { path: '/music-video/:id', name: 'MusicVideo', component: () => import('@/views/MusicVideoView.vue') },
  // Podcasts
  { path: '/podcasts', name: 'Podcasts', component: () => import('@/views/PodcastsView.vue') },
  { path: '/podcasts/new', name: 'PodcastCreate', component: () => import('@/views/PodcastCreateView.vue') },
  { path: '/podcasts/:id/edit', name: 'PodcastEdit', component: () => import('@/views/PodcastCreateView.vue') },
  { path: '/podcasts/:id/analytics', name: 'PodcastAnalytics', component: () => import('@/views/PodcastAnalyticsView.vue') },
  { path: '/podcasts/:id', name: 'PodcastDetail', component: () => import('@/views/PodcastDetailView.vue') },
  { path: '/podcasts/:pid/series/:sid', name: 'PodcastSeries', component: () => import('@/views/PodcastSeriesView.vue') },
  { path: '/personal-information', name: 'PersonalInformationView', component: () => import('@/views/PersonalInformationView.vue') },
  { path: '/drop', name: 'DropView', component: () => import('@/views/DropView.vue') },
  { path: '/collab-project/:id', name: 'CollabProject', component: () => import('@/views/CollabProjectView.vue'), props: true },
  { path: '/collab', name: 'CollabView', component: () => import('@/views/CollabView.vue') },
  { path: '/label-profile', name: 'LabelProfile', component: () => import('@/views/LabelProfileView.vue') },
  { path: '/label-home', name: 'LabelHome', component: () => import('@/views/LabelHomeView.vue') },
  { path: '/artist-home', name: 'ArtistHome', component: () => import('@/views/ArtistHomeView.vue') },
  { path: '/artist-discover', name: 'ArtistDiscover', component: () => import('@/views/ArtistDiscoverView.vue') },
  { path: '/artist-collab', name: 'ArtistCollab', component: () => import('@/views/ArtistCollabView.vue') },
  { path: '/artist-profile', name: 'ArtistProfile', component: () => import('@/views/ArtistProfileView.vue') },
  { path: '/artist-messages', name: 'ArtistMessages', component: () => import('@/views/ArtistMessagesView.vue') },
  { path: '/artist-rooms', name: 'ArtistRooms', component: () => import('@/views/ArtistRoomsView.vue') },
  { path: '/fan-home', name: 'FanHome', component: () => import('@/views/FanHomeView.vue') },
  { path: '/fan-discover', name: 'FanDiscover', component: () => import('@/views/FanDiscoverView.vue') },
  { path: '/fan-rooms', name: 'FanRooms', component: () => import('@/views/FanRoomsView.vue') },
  { path: '/fan-profile', name: 'FanProfile', component: () => import('@/views/FanProfileView.vue') },
  { path: '/fan-messages', name: 'FanMessages', component: () => import('@/views/FanMessagesView.vue') },
  { path: '/rising-home', name: 'RisingHome', component: () => import('@/views/RisingHomeView.vue') },
  { path: '/rising-discover', name: 'RisingDiscover', component: () => import('@/views/RisingDiscoverView.vue') },
  { path: '/rising-collab', name: 'RisingCollab', component: () => import('@/views/RisingCollabView.vue') },
  { path: '/rising-profile', name: 'RisingProfile', component: () => import('@/views/RisingProfileView.vue') },
  { path: '/rising-messages', name: 'RisingMessages', component: () => import('@/views/RisingMessagesView.vue') },
  { path: '/rising-rooms', name: 'RisingRooms', component: () => import('@/views/RisingRoomsView.vue') },
  { path: '/membership', name: 'Membership', component: () => import('@/views/MembershipView.vue') },
  { path: '/album/:id', name: 'AlbumDetail', component: () => import('@/views/AlbumDetailView.vue'), props: true },
  { path: '/playlist/:id', name: 'PlaylistDetail', component: () => import('@/views/PlaylistDetailView.vue'), props: true },
  { path: '/podcast-studio', name: 'PodcasterStudio', component: () => import('@/views/PodcasterStudioView.vue') },
  { path: '/podcaster-messages', name: 'PodcasterMessages', component: () => import('@/views/PodcasterMessagesView.vue') },
  { path: '/podcaster-profile', name: 'PodcasterProfile', component: () => import('@/views/PodcasterProfileView.vue') },
  { path: '/artist-analytics', name: 'ArtistAnalytics', component: () => import('@/views/ArtistAnalyticsView.vue') },
  { path: '/rising-analytics', name: 'RisingAnalytics', component: () => import('@/views/RisingAnalyticsView.vue') },
  { path: '/artist/:id', name: 'PublicArtistProfile', component: () => import('@/views/PublicArtistProfileView.vue'), props: true },
  // Old dashboard URL → the new admin console.
  { path: '/admin-dashboard', redirect: '/admin' },
  {
    path: '/admin',
    component: () => import('@/views/admin/AdminShell.vue'),
    meta: { requiresAdmin: true },
    children: [
      { path: '', name: 'AdminOverview', component: () => import('@/views/admin/AdminOverview.vue'), meta: { title: 'Overview' } },
      { path: 'users', name: 'AdminUsers', component: () => import('@/views/admin/AdminUsers.vue'), meta: { title: 'Users' } },
      { path: 'content', name: 'AdminContent', component: () => import('@/views/admin/AdminContent.vue'), meta: { title: 'Content' } },
      { path: 'rooms', name: 'AdminRooms', component: () => import('@/views/admin/AdminRooms.vue'), meta: { title: 'Live rooms' } },
      { path: 'collabs', name: 'AdminCollabs', component: () => import('@/views/admin/AdminCollabs.vue'), meta: { title: 'Collabs' } },
      { path: 'pools', name: 'AdminPools', component: () => import('@/views/admin/AdminPools.vue'), meta: { title: 'Payout pools' } },
      { path: 'finance', name: 'AdminFinance', component: () => import('@/views/admin/AdminFinance.vue'), meta: { title: 'Finance' } },
      { path: 'moderation', name: 'AdminModeration', component: () => import('@/views/admin/AdminModeration.vue'), meta: { title: 'Moderation' } },
      { path: 'appeals', name: 'AdminAppeals', component: () => import('@/views/admin/AdminAppeals.vue'), meta: { title: 'Appeals' } },
      { path: 'blog', name: 'AdminBlog', component: () => import('@/views/admin/AdminBlog.vue'), meta: { title: 'Blog' } },
      { path: 'blog/new', name: 'AdminBlogNew', component: () => import('@/views/admin/AdminBlogEditor.vue'), meta: { title: 'New post' } },
      { path: 'blog/:id', name: 'AdminBlogEdit', component: () => import('@/views/admin/AdminBlogEditor.vue'), meta: { title: 'Edit post' } },
      { path: 'announcements', name: 'AdminAnnouncements', component: () => import('@/views/admin/AdminAnnouncements.vue'), meta: { title: 'Announcements' } },
      { path: 'system', name: 'AdminSystem', component: () => import('@/views/admin/AdminSystem.vue'), meta: { title: 'System & audit' } },
    ],
  },
  {
    path: '/group/:id/create-post',
    name: 'CreateGroupPost',
    component: () => import('@/views/CreateGroupPost.vue'),
  },  
  {
    path: '/groups/create',
    name: 'CreateGroup',
    component: () => import('@/views/CreateGroupView.vue'),
  },    
  {
    path: '/member',
    name: 'MemberFriendsView',
    component: MemberFriendsView,
    props: true
  },
  {
    path: "/member-profile/:id",
    name: "MemberProfile",
    component: () => import("@/views/MemberProfileView.vue"),
    props: true,
  },
  {
    path: "/member-bio/:id",
    name: "MemberBio",
    component: () => import("@/views/MemberBioView.vue"),
    props: true,
  },  
  {
    path: "/register",
    name: "Register",
    component: () => import("@/views/RegisterView.vue"),
    meta: { public: true },
  },
  // Old sign-up link (bookmarks, emails, shared URLs) → the new page.
  { path: "/waitlist-register", redirect: "/register" },
  {
    path: "/waitlist/complete",
    name: "WaitlistComplete",
    component: () => import("@/views/WaitlistComplete.vue"),
    meta: { public: true },
  },
  { path: "/profile", name: "ProfileView", component: ProfileView },
  { path: "/search", name: "Search", component: SearchView },
  { path: "/messages", name: "MessagesView", component: MessagesView },
  { path: "/chats", name: "ChatView", component: ChatView },
  { path: "/groups", name: "GroupView", component: GroupView },
  { path: "/group-list", name: "GroupListView", component: GroupListView },
  { path: "/add-member", name: "AddMemberView", component: AddMemberView },
  { path: "/posts", name: "PostsView", component: PostsView },
  { path: "/admin-search", name: "AdminSearch", component: AdminSearchView },
  { path: "/member-settings", name: "MemberSettings", component: MemberSettingsView },
  { path: "/admin-settings", name: "AdminSettings", component: AdminSettingsView },
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('@/views/NotificationView.vue') // adjust path if needed
  },  

  // Special Routes
  {
    path: "/groups/:id",
    name: "GroupDetail",
    component: GroupDetailView,
    props: true,
  },
  {
    path: '/admin-messages',
    name: 'AdminMessages',
    component: () => import('@/views/AdminConversationList.vue')
  },
  {
    path: "/member-detail/:id",
    name: "MemberDetail",
    component: MemberDetailView,
    props: true,
  },
  {
    path: "/security-settings",
    name: "SecuritySettingsView",
    component: SecuritySettingsView,
    props: true,
  },
  // Public pages — Google Play and the App Store open these while signed out.
  { path: '/privacy', name: 'privacy', component: PrivacyView, meta: { public: true, title: 'Privacy · Muzilly' } },
  { path: '/appeal', name: 'appeal', component: () => import('@/views/AppealView.vue'), meta: { public: true, title: 'Appeal a decision · Muzilly' } },
  { path: '/appeals', name: 'myAppeals', component: () => import('@/views/MyAppealsView.vue'), meta: { title: 'Appeals · Muzilly' } },
  { path: '/guidelines', name: 'guidelines', component: GuidelinesView, meta: { public: true, title: 'Community guidelines · Muzilly' } },
  { path: '/terms', name: 'terms', component: TermsView, meta: { public: true, title: 'Terms · Muzilly' } },
  { path: '/about', name: 'about', component: AboutView, meta: { public: true, title: 'About · Muzilly' } },
  { path: '/faq', name: 'faq', component: FaqView, meta: { public: true, title: 'FAQ · Muzilly' } },
  { path: '/blog', name: 'Blog', component: () => import('@/views/BlogView.vue'), meta: { public: true, title: 'Blog · Muzilly' } },
  { path: '/blog/:slug', name: 'BlogPost', component: () => import('@/views/BlogPostView.vue'), meta: { public: true } },
  { path: '/delete-account', name: 'delete-account', component: DeleteAccountView, meta: { public: true, title: 'Delete account · Muzilly' } },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

// Route guard. This is UX only: Firestore rules and Cloud Functions re-check every
// read and write on the server. Rules:
//  - Public pages (meta.public) plus the sign-in page "/" are open to everyone.
//  - Every other page needs a signed-in user, otherwise it goes to sign-in.
//  - Admin pages (meta.requiresAdmin, or any /admin* path) also need type "Admin".
const authReady = new Promise((resolve) => {
  const off = onAuthStateChanged(auth, (u) => { off(); resolve(u); });
});
const OPEN_PATHS = ["/", "/register", "/waitlist/complete"];
let adminCache = { uid: null, ok: false };
async function isAdminUser(u) {
  if (adminCache.uid === u.uid) return adminCache.ok;
  const snap = await getDoc(doc(db, "user", u.uid));
  const d = snap.data() || {};
  const ok = String(d.type || d.role || "").toLowerCase() === "admin";
  adminCache = { uid: u.uid, ok };
  return ok;
}
router.beforeEach(async (to, from, next) => {
  const isOpen = OPEN_PATHS.includes(to.path) || to.matched.some((r) => r.meta && r.meta.public);
  if (isOpen) return next();
  try {
    await authReady;
    const u = auth.currentUser;
    if (!u) return next("/");
    const needsAdmin = /^\/admin/.test(to.path) || to.matched.some((r) => r.meta && r.meta.requiresAdmin);
    if (!needsAdmin) return next();
    return (await isAdminUser(u)) ? next() : next("/");
  } catch (e) {
    return next("/");
  }
});

export default router;